const hasScore = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const toNumber = (value) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const getPartFromIndexNumber = (indexNumber) => {
  const [part] = String(indexNumber || "").split(".");
  return part || "-";
};

const sortIndexNumber = (a, b) => {
  const partA = String(a || "")
    .split(".")
    .map((i) => Number(i));
  const partB = String(b || "")
    .split(".")
    .map((i) => Number(i));
  const maxLength = Math.max(partA.length, partB.length);
  for (let i = 0; i < maxLength; i += 1) {
    const valueA = Number.isFinite(partA[i]) ? partA[i] : -1;
    const valueB = Number.isFinite(partB[i]) ? partB[i] : -1;
    if (valueA !== valueB) return valueA - valueB;
  }
  return 0;
};

const getDisplayScore = (supplierRow) => {
  if (!supplierRow) return "-";
  if (!hasScore(supplierRow.score)) return "-";
  return supplierRow.score;
};

export function computeAvgScoreByCategoryAndSupplier(data = {}) {
  const rows = Object.values(data || {});
  const summary = {};

  rows.forEach((question) => {
    const categoryKey = String(question.main_category_id);

    if (!summary[categoryKey]) {
      summary[categoryKey] = {
        main_category_id: question.main_category_id,
        part: getPartFromIndexNumber(question.index_number),
        suppliers: {},
      };
    }

    Object.entries(question.suppliers || {}).forEach(
      ([supplierId, supplier]) => {
        const supplierSummary = summary[categoryKey].suppliers[supplierId] || {
          supplier_id: supplierId,
          supplier_name: supplier.supplier_name || "",
          main_category_id: summary[categoryKey].main_category_id,
          part: summary[categoryKey].part,
          question_count: 0,
          total_score: 0,
          avg_score: 0,
        };

        if (supplier.required_score === true) {
          supplierSummary.question_count += 1;
          supplierSummary.total_score += hasScore(supplier.score)
            ? toNumber(supplier.score_value)
            : 0;
        }

        summary[categoryKey].suppliers[supplierId] = supplierSummary;
      },
    );
  });

  Object.values(summary).forEach((category) => {
    Object.values(category.suppliers).forEach((supplier) => {
      supplier.avg_score =
        supplier.question_count > 0
          ? Number((supplier.total_score / supplier.question_count).toFixed(2))
          : 0;
    });
  });

  return summary;
}

export function computeScoreByCategoryAndSupplier(data = {}) {
  return computeAvgScoreByCategoryAndSupplier(data);
}

export function buildAssessmentCategorySummaryData(data = {}) {
  const questionRows = Object.values(data || {}).sort((a, b) =>
    sortIndexNumber(a.index_number, b.index_number),
  );

  const supplierMap = new Map();
  questionRows.forEach((question) => {
    Object.entries(question.suppliers || {}).forEach(
      ([supplierId, supplier]) => {
        if (!supplierMap.has(supplierId)) {
          supplierMap.set(supplierId, {
            id: supplierId,
            name: supplier.supplier_name,
          });
        }
      },
    );
  });
  const suppliers = Array.from(supplierMap.values()).sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || "")),
  );

  const scoresByCategory = computeAvgScoreByCategoryAndSupplier(data);
  const sections = [];
  const sectionMap = new Map();

  questionRows.forEach((question) => {
    const categoryKey = String(question.main_category_id);

    if (!sectionMap.has(categoryKey)) {
      const section = {
        key: categoryKey,
        mainCategoryId: question.main_category_id,
        mainCategoryName: question.main_category_name || "",
        questions: [],
        avgRow: { label: "Avg Score", cells: [] },
      };
      sectionMap.set(categoryKey, section);
      sections.push(section);
    }

    const section = sectionMap.get(categoryKey);
    section.questions.push({
      questionId: question.question_id,
      indexNumber: question.index_number,
      cells: suppliers.map((supplier) => {
        const supplierRow = question.suppliers?.[supplier.id];
        const displayScore = getDisplayScore(supplierRow);
        const missingRequiredScore =
          supplierRow &&
          supplierRow.required_score === true &&
          displayScore === "-";

        return {
          displayScore,
          scoreColorKey: supplierRow?.score,
          missingRequiredScore,
          gradeForExport: displayScore,
          valueForExport:
            displayScore === "-" ? "-" : toNumber(supplierRow.score_value),
        };
      }),
    });
  });

  sections.forEach((section) => {
    section.avgRow.cells = suppliers.map((supplier) => {
      const supplierScore = scoresByCategory?.[section.key]?.suppliers?.[supplier.id];
      const avgDisplay =
        supplierScore && supplierScore.question_count > 0
          ? supplierScore.avg_score.toFixed(2)
          : "-";

      return {
        displayScore: avgDisplay,
        gradeForExport: avgDisplay,
        valueForExport: avgDisplay,
      };
    });
    section.title = `${section.mainCategoryName || `Category ${section.mainCategoryId}`} (${section.questions.length} questions)`;
  });

  const overallStatsBySupplier = {};
  questionRows.forEach((question) => {
    suppliers.forEach((supplier) => {
      const supplierRow = question.suppliers?.[supplier.id];
      if (!supplierRow || supplierRow.required_score !== true) return;

      if (!overallStatsBySupplier[supplier.id]) {
        overallStatsBySupplier[supplier.id] = {
          questionCount: 0,
          totalScore: 0,
        };
      }

      overallStatsBySupplier[supplier.id].questionCount += 1;
      overallStatsBySupplier[supplier.id].totalScore += hasScore(supplierRow.score)
        ? toNumber(supplierRow.score_value)
        : 0;
    });
  });

  const overallAvgRow = {
    label: "Overall Avg Score",
    cells: suppliers.map((supplier) => {
      const stats = overallStatsBySupplier[supplier.id];
      const avgDisplay =
        stats && stats.questionCount > 0
          ? (stats.totalScore / stats.questionCount).toFixed(2)
          : "-";
      return {
        displayScore: avgDisplay,
        gradeForExport: avgDisplay,
        valueForExport: avgDisplay,
      };
    }),
  };

  return {
    suppliers,
    sections,
    overallAvgRow,
    scoresByCategory,
  };
}
